import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Loader2, PackageCheck } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const today = () => new Date().toISOString().slice(0, 10);

/** Panels of one pallet — opened from a scanned QR code or a pallet link. */
export function PalletDetailDialog({
  projectId,
  palletCode,
  open,
  onOpenChange,
  canConfirm,
  onDone,
}) {
  const [panels, setPanels] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !palletCode) return;
    setPanels(null);
    api
      .get(`/projects/${projectId}/panels`, { params: { pallet_code: palletCode } })
      .then((res) => setPanels(res.data.panels))
      .catch((error) => toast.error(apiError(error)));
  }, [open, palletCode, projectId]);

  const pending = (panels || []).filter(
    (p) => p.statuses?.logistics === "Shipped"
  );
  const deliveredCount = (panels || []).filter((p) => p.statuses?.logistics === "Arrived").length;
  const pallet = (panels || [])[0] || {};

  const confirmAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/panels/delivery/bulk`, {
        panel_uids: pending.map((p) => p.panel_uid),
        delivered_on: today(),
      });
      toast.success(
        `${data.updated.length} panel${data.updated.length === 1 ? "" : "s"} marked as Arrived`
      );
      onOpenChange(false);
      onDone?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pallet-detail-dialog"
        className="max-w-lg border-slate-200 bg-white text-slate-800"
      >
        <DialogHeader>
          <DialogTitle className="mono text-lg tracking-tight">
            Pallet {palletCode}
          </DialogTitle>
        </DialogHeader>

        {panels === null ? (
          <div className="grid h-24 place-items-center">
            <Loader2 size={18} className="animate-spin text-cyan-600" />
          </div>
        ) : panels.length === 0 ? (
          <p className="py-4 text-sm text-slate-500">
            No panels are assigned to this pallet.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-[11.5px]">
              {[
                ["Panels", panels.length],
                ["In transit", pending.length],
                ["Arrived", deliveredCount],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-slate-200 px-3 py-2">
                  <p className="mono text-lg text-slate-900">{value}</p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                </div>
              ))}
            </div>
            <p className="mono text-[10.5px] uppercase tracking-wider text-slate-500">
              {pallet.shipment_code || "no shipment code"} · {pallet.shipping_method || "—"} · ETA{" "}
              {(pallet.estimated_arrival || "—").slice(0, 10)}
            </p>
            <div className="okn-scroll max-h-56 overflow-y-auto rounded-xl border border-slate-200 p-3">
              <div className="flex flex-wrap gap-1.5">
                {panels.map((panel) => {
                  const done = panel.statuses?.logistics === "Arrived";
                  return (
                    <span
                      key={panel.panel_uid}
                      data-testid={`pallet-panel-${panel.panel_name}`}
                      className={`mono rounded px-1.5 py-0.5 text-[10px] ring-1 ${
                        done
                          ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/40"
                          : "bg-cyan-500/10 text-cyan-800 ring-cyan-500/40"
                      }`}
                    >
                      {panel.panel_name}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <a
                data-testid="pallet-detail-label-pdf"
                href={`${API}/projects/${projectId}/exports/pallet-labels.pdf?pallet_code=${encodeURIComponent(palletCode)}`}
                target="_blank"
                rel="noreferrer"
                className="mono flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 py-2.5 text-[10.5px] uppercase tracking-wider text-slate-700 hover:border-cyan-400 hover:text-cyan-700"
              >
                <FileDown size={13} /> Label PDF
              </a>
              {canConfirm ? (
                <Button
                  data-testid="pallet-confirm-delivery-button"
                  onClick={confirmAll}
                  disabled={busy || pending.length === 0}
                  className="h-10 flex-1 rounded-full bg-cyan-600 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500 disabled:opacity-60"
                >
                  {busy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <>
                      <PackageCheck size={14} className="mr-2" />
                      Mark {pending.length} as Arrived
                    </>
                  )}
                </Button>
              ) : null}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
